import { setOrderPartRejectedByClient } from '$api/evoAPIs'
import { PartNoBg } from '$pages/serviceuser/Marketplace/Bookservice/Defaultwizard/styledComponents'
import { StyledSubTable } from './styledComponents'
import { DeleteOutlined } from '@ant-design/icons'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { Popconfirm, Tag } from 'antd'
import Tooltip from '$components/Tooltip'
import { useTranslation } from 'react-i18next'
import {
  calculateOrderPartUnitPriceRange,
  calculatePricePerUnit,
  summarizeDeliveryTimes,
} from '../utils'
import OrderPartDetailView from './OrderPartDetailView'
import { StyledLink } from './styledComponents'
import { currencyRenderer, getColorLabel } from '$utils/i18n'
import TruncateText from '$components/TruncateText'

const OrderedPartsTable = ({
  orderPartsList,
  orderPartFormData,
  resetOrderPartFormData,
  orderPartListStatus,
  loading,
  editable = false,
  showProviderOffers,
  showRejectedProviderOffers,
  showOfferTerms,
  additionalColumns = [],
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  if (!orderPartsList.provider_accepted_offer) {
    editable = false
  }

  // once client has accepted offer, rejected parts of offer should
  // - still be rendered
  // - be visually recognizable as rejected
  // - be excluded from price calculations etc.
  const disableRejectedProviderOffers =
    orderPartsList.client_accepted_offer && showRejectedProviderOffers

  const allowOrderPartReject =
    orderPartsList.list_of_orderparts.filter(
      (orderPart) => !orderPart.rejected_by_client,
    ).length > 1

  const validOrderParts = showRejectedProviderOffers
    ? orderPartsList.list_of_orderparts
    : orderPartsList.list_of_orderparts.filter(
        (orderPart) => !orderPart.rejected_by_client,
      )

  const rejectOfferMutation = useMutation({
    mutationFn: setOrderPartRejectedByClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allorderedpartslists'] })
    },
  })

  const handleDelete = (orderPartId) => {
    if (allowOrderPartReject) {
      rejectOfferMutation.mutate({
        order_part_id: orderPartId,
        rejected_by_client: true,
      })
    }
  }

  const defaultColumns = [
    // this is for space
    ...(!showOfferTerms
      ? [
          {
            title: '',
          },
        ]
      : []),
    {
      title: t('Part No'),
      width: '160px',
      render: (record) => (
        <PartNoBg
          disabled={disableRejectedProviderOffers && record.rejected_by_client}
        >
          {record.part.id_part_client}
        </PartNoBg>
      ),
    },
    {
      title: t('Part Name'),
      render: (record) => {
        const partName = record.part.name || t('unknown')
        return (
          <TruncateText
            text={partName}
            useLineClamp={true}
            style={
              record.status_info == 'discarded'
                ? { textDecoration: 'line-through' }
                : {}
            }
            addedTooltip={
              record.status_info == 'discarded'
                ? t('service.order_part.tooltip.offer_discarded')
                : ''
            }
          />
        )
      },
      ellipsis: true,
    },
    {
      title: t('Amount'),
      render: (record) => {
        // return orderPartsList.main_order.orderpart_quantity[record.id]

        const selectedOffers =
          record.provider_offers?.filter((offer) => offer.selected === true) ||
          []
        if (selectedOffers) {
          return selectedOffers
            .map((offer) => offer.amount)
            .reduce((partialSum, a) => partialSum + a, 0)
        }

        return Object.values(
          record.status_info !== 'discarded' &&
            orderPartFormData[record.id].provider_offers,
        )
          .map((offer) => offer.amount)
          .reduce((partialSum, a) => partialSum + a, 0)
      },
    },
    {
      title: t('additive_material_column'),
      render: (record) => {
        const materialName =
          record.operator_additive_material?.material_name || t('unknown')

        const tooltipText = !record.is_oam_changed
          ? t('additive_material_column_tooltip')
          : ''

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              overflow: 'hidden',
              minWidth: 0,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <TruncateText
                text={`${materialName}${getColorLabel(record.selected_color, t)}`}
                addedTooltip={tooltipText}
              />
            </div>
            {record.is_oam_changed && (
              <Tooltip
                title={t(
                  'material_changed_by_operator',
                  'Material changed by operator',
                )}
              >
                <Tag color="orange" style={{ margin: 0 }}>
                  v2
                </Tag>
              </Tooltip>
            )}
          </div>
        )
      },
      ellipsis: true,
    },
    {
      title: t('my_orders.delivery_time'),
      render: (record) => {
        if (!showOfferTerms) {
          return
        }

        const selectedOffers =
          record.provider_offers?.filter((offer) => offer.selected === true) ||
          []
        if (selectedOffers.length > 0) {
          const deliveryDays = selectedOffers
            .map((offer) => offer.delivery_days)
            .filter((days) => days !== null && days !== undefined)

          if (deliveryDays.length > 0) {
            const maxDeliveryDays = Math.max(...deliveryDays)
            return maxDeliveryDays + ' ' + t('days')
          }
        }

        let deliveryTimes = []
        if (editable && orderPartFormData[record.id]) {
          deliveryTimes = Object.values(
            orderPartFormData[record.id].provider_offers || {},
          )
            .map((offer) => offer.delivery_days || offer.base_production_time)
            .filter((days) => days)
        } else {
          deliveryTimes =
            record.provider_offers
              ?.map((offer) =>
                orderPartsList.client_accepted_offer
                  ? offer.delivery_days
                  : [
                      offer.base_production_time,
                      ...offer.production_time_rules.map(
                        (rule) => rule.max_days,
                      ),
                    ],
              )
              .flat() || []
        }
        return t(
          'received_requests.days',
          summarizeDeliveryTimes(deliveryTimes),
        )
      },
    },
    {
      title: t('Price per unit'),
      render: (record) => {
        if (!showOfferTerms) {
          return
        }

        const selectedOffers =
          record.provider_offers?.filter((offer) => offer.selected === true) ||
          []

        if (selectedOffers.length == 1) {
          return currencyRenderer(t)(selectedOffers[0].base_price)
        } else if (selectedOffers.length > 1) {
          const { min, max } = calculateOrderPartUnitPriceRange(selectedOffers)
          return min === max
            ? currencyRenderer(t)(min)
            : `${currencyRenderer(t)(min)} – ${currencyRenderer(t)(max)}`
        }

        let minPricePerUnit
        let maxPricePerUnit
        let pricesPerUnit = []
        if (editable) {
          const providerOffers = Object.values(
            record.status_info !== 'discarded' &&
              orderPartFormData[record.id].provider_offers,
          )
          pricesPerUnit = providerOffers.map((offer) =>
            calculatePricePerUnit(offer),
          )
        } else {
          pricesPerUnit =
            record.status_info !== 'discarded' &&
            record.provider_offers.map((offer) =>
              calculatePricePerUnit(
                offer,
                orderPartsList.client_accepted_offer,
              ),
            )
        }

        if (pricesPerUnit.length > 0) {
          minPricePerUnit = Math.min(...pricesPerUnit)
          maxPricePerUnit = Math.max(...pricesPerUnit)
        }

        return minPricePerUnit === maxPricePerUnit
          ? currencyRenderer(t)(minPricePerUnit)
          : `${currencyRenderer(t)(minPricePerUnit)} – ${currencyRenderer(t)(maxPricePerUnit)}`
      },
    },
    ...(editable
      ? [
          {
            title: t('Status'),
            render: (record) => {
              if (
                orderPartListStatus?.[orderPartsList.id]?.includes(record.id)
              ) {
                return (
                  <Tooltip title={t('service.order_part.tooltip.reviewed')}>
                    <Tag color="success">
                      <TruncateText
                        text={t('service.order_part.status.reviewed')}
                      />
                    </Tag>
                  </Tooltip>
                )
              } else {
                return (
                  <Tooltip title={t('service.order_part.tooltip.review')}>
                    <Tag>
                      <TruncateText
                        text={t('service.order_part.status.review')}
                      />
                    </Tag>
                  </Tooltip>
                )
              }
            },
          },
          {
            title: '',
            render: (record) =>
              allowOrderPartReject && (
                <Popconfirm
                  overlayStyle={{
                    width: '270px',
                  }}
                  title={t('service.order_part.confirm_delete')}
                  onConfirm={() => handleDelete(record.id)}
                >
                  <StyledLink>
                    <DeleteOutlined />
                  </StyledLink>
                </Popconfirm>
              ),
          },
        ]
      : []),
  ]

  return (
    <StyledSubTable
      size="middle"
      columns={[...defaultColumns, ...additionalColumns]}
      style={{ width: '100%' }}
      dataSource={validOrderParts}
      loading={loading}
      rowKey={(record) => record.id}
      rowClassName={(record) =>
        disableRejectedProviderOffers && record.rejected_by_client
          ? 'row-disabled'
          : undefined
      }
      expandable={
        showProviderOffers
          ? {
              showExpandColumn: true,
              expandRowByClick: true,
              expandedRowClassName: (record) =>
                disableRejectedProviderOffers && record.rejected_by_client
                  ? 'expanded-row-disabled'
                  : undefined,
              expandedRowRender: (record) => (
                <OrderPartDetailView
                  orderPartListId={orderPartsList.id}
                  clientAcceptedOffer={orderPartsList.client_accepted_offer}
                  orderPart={record}
                  resetFormData={() => resetOrderPartFormData(record.id)}
                  editable={editable}
                />
              ),
            }
          : false
      }
    />
  )
}
export default OrderedPartsTable
